import { type Entities, type MenuItem, ScreenType } from "@client/types";
import { type PageInfo } from "@client/types/AppState/pages";
import { isStaffOrSuperUser } from "@common/appInfo";
import { getEntitySelector } from "./layout";
import { getInherittedSettingValue, getVisibility } from "./settings";
import { buildTree, traverseNodes, traverseTree } from "./tree";

const cloneMenu = (node: MenuItem): MenuItem => ({
  ...node,
  children: node.children.map(cloneMenu)
});

const cutChildren = (node: MenuItem, level?: number): void => {
  if (!level) {
    node.children = [];
    return;
  }

  node.children && node.children.forEach((child) => cutChildren(child as MenuItem, level - 1));
};

export interface FilterMenuArgs {
  currentOnly: boolean;
  cutChildren: boolean;
  pages?: Array<string>;
  level?: number;
}

export const GET_FAKE_ROOT_ITEM = (children: MenuItem[]): MenuItem => ({
  id: "".toString(),
  parentId: null,
  name: "",
  label: "",
  link: "",
  isEnabled: false,
  isPermanentRedirect: false,
  openInNewWindow: false,
  isCurrent: false,
  sortOrder: -1,
  children
});

/**
 * Modifies menu items by removing children.
 * @param args - pages - ids/names of affected pages. undefined - all pages are affected. Note, pages can be anywhere in the menu tree, not just root level items
 *               level - 0 - cut all children, 1 - cut children of children, etc.
 */
export const filterMenu = (menuItems: MenuItem[], args: FilterMenuArgs): Array<MenuItem> => {
  let resultItems: MenuItem[] = menuItems.slice().map((x) => ({ ...x, parentId: x.parentId ?? "" }));
  if (args.cutChildren) {
    const fakeRoot: MenuItem = GET_FAKE_ROOT_ITEM(menuItems);
    const clone = cloneMenu(fakeRoot);

    let targetMenuItems: Array<MenuItem> = [];
    if (args.pages) {
      const pages = new Set<string>(args.pages.map((p) => p.toUpperCase()));
      traverseTree(clone, (node: MenuItem) => {
        if (pages.has(node.id.toString()) || pages.has(node.name.toUpperCase())) {
          targetMenuItems.push(node);
        }
      });
    } else {
      targetMenuItems = clone.children || [];
    }

    targetMenuItems.forEach((node) => cutChildren(node, args.level));

    resultItems = clone.children!;
  }

  if (args.currentOnly) {
    const currentItem = resultItems.find((item) => !!item.isCurrent); // note: it's a valid case when there's no active menu item
    resultItems = (currentItem && currentItem.children) || [];
  }

  return resultItems;
};

export type DotsMenuItem = {
  type: string;
  title: string;
};
export const getDotsMenuItems = (
  items: Array<DotsMenuItem | null>,
  childrenIds: string[],
  entities: Entities,
  screen: ScreenType
): MenuItem[] => {
  let result: MenuItem[] = [];

  let blockIndex = 0;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (!item) continue;

    let label: string = "";
    let link: string;
    if (item.type === "section") {
      if (blockIndex >= childrenIds.length) continue;

      let id = childrenIds[blockIndex++];
      let entity = entities[id];
      let isVisible = true;
      while (true) {
        if (!entity.settings) break;

        isVisible = getVisibility(getInherittedSettingValue(entity.settings, "", "visible", screen)).isVisible;

        if (isVisible || blockIndex === childrenIds.length) break;

        id = childrenIds[blockIndex++];
        entity = entities[id];
      }
      if (!isVisible) continue;

      link = getEntitySelector(id);
      label = entity.data?.name;
    } else {
      if (!item.title) continue;

      link = item.type;
    }

    result.push({
      id: i.toString(),
      isEnabled: true,
      isPermanentRedirect: false,
      label: item.title || label,
      link,
      parentId: null,
      name: item.title,
      openInNewWindow: false,
      isCurrent: false,
      sortOrder: i,
      children: []
    });
  }

  return result;
};

export const buildMenu = (pages: PageInfo[], activePageId: string): MenuItem[] => {
  const items: MenuItem[] = pages
    .filter((x) => (x.isNavigation ?? true) && !x.isDeleted && ((x.isPublished ?? true) || isStaffOrSuperUser()))
    .map((x) => ({
      ...x,
      link: x.path,
      isEnabled: x.isEnabled ?? true,
      label: x.name,
      isPermanentRedirect: false,
      openInNewWindow: false,
      parentId: x.parentId,
      isCurrent: x.id === activePageId,
      children: []
    }));

  const tree = buildTree(items, (items) => items.sort((a, b) => a.sortOrder - b.sortOrder));
  traverseNodes(tree, undefined, (p, c) => {
    if (p && !p.isCurrent) p.isCurrent = c.isCurrent;
  });
  return tree;
};
